/* eslint-disable jira/filename-case */
import { captureException } from '@jira/platform__observability-capture-exception/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { saveStats } from '../stats.tsx';
import { loadBridgeRace } from './load-bridge-race.tsx';
import { loadWrmResources } from './load-wrm-resources.tsx';
import { SHOULD_LOG_ERROR } from './shared.tsx';
import { isWRMHeritageLoaded } from './wait-for-heritage.tsx';

export type LoadBridgeStrictOptions = {
	/** AMD name of the resource being loaded */
	name: string;
	/** WRM keys of the module that implements the amd resource */
	wrmKeys: string[];
	shouldLogError?: boolean;
	/**
	 * Indicates if depends on WRM heritage or is isolated
	 * @default false
	 * @deprecated - marked as deprecated to discourage usage. Please check the actual implementation of the requested module
	 */
	isolatedFromHeritage?: boolean;
};

/**
 * Load an AMD module if the system has already loaded it, or fallback to loading from given WRM
 * keys without retry.
 * This will only load WRM when the named module is not present.
 *
 * @throws Error
 */
export const loadBridgeStrict = async <T,>({
	name,
	wrmKeys,
	shouldLogError = SHOULD_LOG_ERROR,
	isolatedFromHeritage = false,
}: LoadBridgeStrictOptions): Promise<T> => {
	saveStats(name, wrmKeys);
	if (
		// FIXME: remove fg after initial rollout
		// eslint-disable-next-line jira/ff/no-preconditioning
		!isolatedFromHeritage &&
		fg('jfp_magma_jira-report-early-strict-bridge') &&
		getMeta('ajs-environment') === 'staging'
	) {
		if (!isWRMHeritageLoaded()) {
			captureException(
				`loadBridgeStrict:${name}`,
				new Error('Do not call loadBridgeStrict without waitForHeritage'),
			);
		}
	}
	try {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, jira/jira-ssr/no-unchecked-globals-usage
		return window.require(name) as T;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		await loadWrmResources({ wrmKeys, shouldLogError });
		return loadBridgeRace<T>({ name, shouldLogError });
	}
};
